import * as React from "react";
import { AlertCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface InlineErrorProps {
  message: string;
  className?: string;
  onDismiss?: () => void;
  /** Taille du composant */
  size?: "sm" | "md";
}

/**
 * Composant pour afficher les erreurs de validation inline
 * Utilisé sous les champs de formulaire ou dans les sections
 */
export function InlineError({
  message,
  className,
  onDismiss,
  size = "sm"
}: InlineErrorProps) {
  if (!message) return null;

  return (
    <div
      role="alert"
      className={cn(
        "flex items-center gap-2 rounded-md border border-red-200 bg-red-50 text-red-700",
        size === "sm" ? "px-3 py-2 text-sm" : "px-4 py-3",
        "dark:border-red-800/50 dark:bg-red-950/30 dark:text-red-400",
        "animate-in fade-in-0 slide-in-from-top-1 duration-200",
        className
      )}
    >
      <AlertCircle className={cn("shrink-0", size === "sm" ? "h-4 w-4" : "h-5 w-5")} />
      <span className="flex-1">{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-sm p-0.5 hover:bg-red-100 dark:hover:bg-red-900/50"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

interface InlineWarningProps {
  message: string;
  className?: string;
  onDismiss?: () => void;
  size?: "sm" | "md";
}

/**
 * Composant pour afficher les avertissements inline
 * Utilisé pour les warnings non-bloquants
 */
export function InlineWarning({
  message,
  className,
  onDismiss,
  size = "sm"
}: InlineWarningProps) {
  if (!message) return null;

  return (
    <div
      role="alert"
      className={cn(
        "flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 text-amber-700",
        size === "sm" ? "px-3 py-2 text-sm" : "px-4 py-3",
        "dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-400",
        "animate-in fade-in-0 slide-in-from-top-1 duration-200",
        className
      )}
    >
      <AlertCircle className={cn("shrink-0", size === "sm" ? "h-4 w-4" : "h-5 w-5")} />
      <span className="flex-1">{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-sm p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/50"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
